import React, { useState, useEffect, useRef } from "react";
import styles from "./FoodGallery.module.css";
import { db } from "../../firebase"; 
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";

const FoodGallery = () => {
    const [images, setImages] = useState([]);
    const [active, setActive] = useState(0);
    const intervalRef = useRef(null);

    // 🔥 1. Real-time Fetch from Firestore
    useEffect(() => {
        const q = query(
            collection(db, "food-gallery"),
            orderBy("createdAt", "desc")
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const imageData = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));
            setImages(imageData);
        });

        return () => unsubscribe();
    }, []);

    // 🕒 2. Auto slide logic
    const startAuto = () => {
        stopAuto();
        if (images.length > 0) {
            intervalRef.current = setInterval(() => {
                setActive((prev) => (prev === images.length - 1 ? 0 : prev + 1));
            }, 3000);
        }
    };

    const stopAuto = () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
    };

    const prev = () => {
        stopAuto();
        setActive((prev) => (prev === 0 ? images.length - 1 : prev - 1));
        startAuto();
    };

    const next = () => {
        stopAuto();
        setActive((prev) => (prev === images.length - 1 ? 0 : prev + 1));
        startAuto();
    };

    useEffect(() => {
        if (images.length > 0) startAuto();
        return stopAuto;
    }, [images, active]);

    if (images.length === 0) {
        return (
            <section className={styles.section}>
                <div className={styles.container}>
                    <h2 className={styles.title}>OUR CULINARY MOMENTS</h2>
                    <p style={{ color: "#fff", textAlign: "center" }}>Loading moments...</p>
                </div>
            </section>
        );
    }

    return (
        <section className={styles.section}>
            <div className={styles.container}>
                <h2 className={styles.title}>OUR CULINARY MOMENTS</h2>

                <div className={styles.carouselWrapper}>
                    <button className={`${styles.arrow} ${styles.prevArrow}`} onClick={prev}>
                        &#10094;
                    </button>

                    <div
                        className={styles.track}
                        onMouseEnter={stopAuto}
                        onMouseLeave={startAuto}
                    >
                        {images.map((img, i) => {
                            let position = styles.hidden;

                            if (i === active) position = styles.activeSlide;
                            else if (i === (active - 1 + images.length) % images.length)
                                position = styles.prevSlide;
                            else if (i === (active + 1) % images.length)
                                position = styles.nextSlide;
                            else if (i === (active - 2 + images.length) % images.length)
                                position = styles.farPrev;
                            else if (i === (active + 2) % images.length)
                                position = styles.farNext;

                            return (
                                <div key={img.id} className={`${styles.slide} ${position}`}>
                                    <div className={styles.card}>
                                        <img 
                                            /* ADDED: v=1 query for CDN cache control */
                                            src={`${img.imageUrl}?v=1`} 
                                            alt="Food Gallery" 
                                            /* ADDED: loading="lazy" prevents browser from fetching 
                                               off-screen images until necessary */
                                            loading="lazy" 
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <button className={`${styles.arrow} ${styles.nextArrow}`} onClick={next}>
                        &#10095;
                    </button>
                </div>

                <div className={styles.dots}>
                    {images.map((_, idx) => (
                        <span
                            key={idx}
                            className={`${styles.dot} ${active === idx ? styles.activeDot : ""}`}
                            onClick={() => {
                                stopAuto();
                                setActive(idx);
                                startAuto();
                            }}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default FoodGallery;